import React, { useState, useEffect, useMemo } from "react"
import * as uuid from "uuid"
import * as httpx from "httpx"
import * as authzc from "authzcached"
import * as layouts from "layouts"
import * as inputs from "inputs"
import * as sessions from "sessions"
import * as brands from "brands"
import * as api from "brandguard/api"
import * as predictions from "brandguard/predictions"
import * as bglayouts from "brandguard/components/layouts"
import * as bgscores from "brandguard/display/scores"
import * as bgdocuments from "brandguard/display/brandguard/documents"
import * as bgapprovals from "brandguard/display/approval"
import * as pagecache from "brandguard/display/brandguard/documents/cache"
import { UploadSimple } from "brandguard/components"
import * as authz from "authz"
import classnames from "classnames"
import * as nav from "brandguard/components/navigation"
import * as icons from "icons"
import * as scores from "brandguard/components/layouts/scores"
import { styledscroll } from "brandguard/components/layouts"
import { useSettings } from "accounts/settings"
import { State } from "accounts/api"
import styled from "@emotion/styled"
import { CancellablePromise } from "real-cancellable-promise"
import { FeedbackPanel } from "brandguard/feedback"

const OverlayContainer = styled(layouts.containers.absolute)`
	flex-direction: column;
`
OverlayContainer.defaultProps = {
	display: "flex",
	top: "0",
	left: "0",
	width: "100%",
	height: "100%",
}

const PDFContainer = styled(layouts.containers.flex)`
	width: 100%;
	height: 100%;
	align-items: center;
	justify-content: center;
	overflow: auto;
`

export default function Display(props: React.PropsWithChildren<unknown>): JSX.Element {
	const authzaccount = authzc.useCache((cached) => cached.meta)
	const permission = authzaccount.current
	const bearertoken = sessions.useToken()
	const brand = brands.caching.useCached()
	const settings = useSettings()
	const [loading, setLoading] = useState(true)
	const [selectedItem, setSelectedItem] = useState(undefined as api.DocumentSearchResponseItem | undefined)
	const [currentPageIndex, setCurrentPageIndex] = useState(0)
	const [uploadFocused, setUploadFocused] = useState(false)
	const [isRevising, setIsRevising] = useState(false)
	const [undoItems, setUndoItems] = useState<api.DocumentSearchResponseItem[]>([])
	const [versionHistory, setVersionHistory] = useState<api.DocumentSearchResponseItem[]>([])

	const [searchReq] = useState(
		api.documents.uploads.searches.zero({
			offset: 0n,
			limit: 50n,
			brand_id: brand.id,
			queues: [uuid.NIL, brand.id],
			approved: [api.Prediction.APPROVED, api.Prediction.INREVIEW, api.Prediction.REJECTED],
		}),
	)

	const [documents, setDocuments] = useState({
		items: [],
		next: searchReq,
	} as api.DocumentSearchResponse)

	useEffect(() => {
		if (searchReq.brand_id === uuid.NIL) return
		if (!loading) setLoading(true)
		const retry = httpx.autoretry()
		const p = retry
			.wrap(() => api.documents.uploads.search(searchReq.brand_id, searchReq, bearertoken))
			.then((r) => {
				setDocuments(r)
				setLoading(false)
			})
			.catch(httpx.errors.cancellation(() => undefined))
			.catch((cause) => {
				console.error("unable to display brandguard documents", cause)
				setLoading(false)
			})
		return p.cancel
	}, [searchReq])

	useEffect(() => {
		if (documents.items.length > 0) {
			if (selectedItem === undefined) {
				setSelectedItem(documents.items[0])
				setCurrentPageIndex(0)
			}
		} else {
			setSelectedItem(undefined)
		}
	}, [documents])

	useEffect(() => {
		if (!selectedItem) {
			setVersionHistory([])
			return
		}
		const p = api.documents.uploads
			.versions(selectedItem.media!.id, brand.id, bearertoken)
			.then((resp) => setVersionHistory(resp.versions))
			.catch(httpx.errors.cancellation(() => undefined))
			.catch((cause) => console.warn("unable to fetch version history", cause))
		return p.cancel
	}, [selectedItem?.media?.id])

	const onDocumentUpdate = (item: api.DocumentSearchResponseItem): void => {
		setDocuments((prevState) => ({
			...prevState,
			items: prevState.items.map((i) => (i.media?.id === item.media?.id ? item : i)),
		}))
	}

	const agree = async (
		item: api.DocumentSearchResponseItem & { event: api.DocumentEvent },
	): Promise<api.DocumentSearchResponseItem> =>
		api.documents.approval.agree(item, authzc.bearer(authzaccount)).then((res_i) => {
			setUndoItems([...undoItems, ...[item]])
			setDocuments((prevState) => ({
				...prevState,
				items: prevState.items.map((i) => (i.media?.id === res_i.media?.id ? res_i : i)),
			}))
			if (selectedItem && selectedItem.media?.id === res_i.media?.id) setSelectedItem(res_i)
			return res_i
		})

	const disagree = (
		item: api.DocumentSearchResponseItem & { event: api.DocumentEvent },
	): Promise<api.DocumentSearchResponseItem> =>
		api.documents.uploads
			.patch(
				item.event.brand_id,
				item.event.id,
				{
					...item,
					media: { ...item.media!, tombstoned_at: new Date().toISOString() },
					event: { ...item.event, observation: api.Prediction.DISCARDED_BY_UPLOADER },
				},
				authzc.bearer(authzaccount),
			)
			.then((res_i) => {
				setUndoItems([...undoItems, ...[item]])
				setDocuments((prevState) => ({
					...prevState,
					items: prevState.items.map((i) => (i.media?.id === res_i.media?.id ? res_i : i)),
				}))
				if (selectedItem && selectedItem.media?.id === res_i.media?.id) setSelectedItem(res_i)
				return res_i
			})

	const undo = (
		_current: api.DocumentSearchResponseItem & { event: api.DocumentEvent },
		previously: api.DocumentSearchResponseItem & { event: api.DocumentEvent },
	): Promise<api.DocumentPatchResponse> => {
		return api.documents.uploads
			.patch(previously.event.brand_id, previously.event.id, previously, authzc.bearer(authzaccount))
			.then((resp_i) => {
				setUndoItems(undoItems.filter((i) => i.media?.id !== previously.media?.id))
				setDocuments((prevState) => ({
					...prevState,
					items: prevState.items.map((i) => (i.media?.id === previously.media?.id ? previously : i)),
				}))
				if (selectedItem && selectedItem.media?.id === previously.media?.id) setSelectedItem(previously)
				return resp_i
			})
	}

	// Horizontal navigation: between documents
	const nextDocument = () => {
		const nextItem = documents.items[(documents.items.indexOf(selectedItem!) + 1) % documents.items.length]
		setSelectedItem(nextItem)
		setCurrentPageIndex(0) // Reset to first page of new document
	}

	const prevDocument = () => {
		const prevItem = documents.items[(documents.items.indexOf(selectedItem!) - 1 + documents.items.length) % documents.items.length]
		setSelectedItem(prevItem)
		setCurrentPageIndex(0) // Reset to first page of new document
	}

	// Vertical navigation: between pages within a document
	const nextPage = () => {
		if (selectedItem && selectedItem.pages) {
			setCurrentPageIndex((prev) => Math.min(prev + 1, selectedItem.pages.length - 1))
		}
	}

	const prevPage = () => {
		setCurrentPageIndex((prev) => Math.max(prev - 1, 0))
	}

	const handleKeyDown = (evt: KeyboardEvent) => {
		if (evt.key === "ArrowRight") {
			nextDocument()
		} else if (evt.key === "ArrowLeft") {
			prevDocument()
		} else if (evt.key === "ArrowDown") {
			nextPage()
		} else if (evt.key === "ArrowUp") {
			prevPage()
		}
	}

	useEffect(() => {
		if (!selectedItem) return
		window.addEventListener("keydown", handleKeyDown)
		return () => {
			window.removeEventListener("keydown", handleKeyDown)
		}
	}, [selectedItem, currentPageIndex])

	const currentPage = selectedItem?.pages?.[currentPageIndex]
	const profile = sessions.useProfile()
	const account = sessions.useAccount()
	const [pageImageURL, setPageImageURL] = useState("")
	const [pageImageLoading, setPageImageLoading] = useState(false)

	// Get stable page ID to avoid unnecessary effect triggers
	const currentPageId = currentPage?.id

	// Fetch signed URL for current page image
	useEffect(() => {
		if (!currentPageId) {
			setPageImageURL("")
			setPageImageLoading(false)
			return
		}

		setPageImageLoading(true)
		const p = pagecache
			.current(account.id, currentPageId, bearertoken)
			.then((url) => {
				setPageImageURL(url)
				setPageImageLoading(false)
			})
			.catch((cause) => {
				console.error("unable to load document page image", cause)
				setPageImageURL("")
				setPageImageLoading(false)
			})

		return () => {
			if (p && typeof p.cancel === 'function') {
				p.cancel()
			}
		}
	}, [currentPageId])

	const MemoUpload = useMemo(() => {
		return function MemodUpload(props: inputs.uploads.api.Props) {
			return (
				<authz.Protected
					enabled={permission.brandguard_upload}
					rejected={
						<authz.layouts.UploadDenied>
							You do not have permissions to upload documents. Please contact your workspace administrator to
							request access.
						</authz.layouts.UploadDenied>
					}
				>
					<bgdocuments.uploads.default
						background={layouts.theme.colors.blue.blue}
						observation={api.Prediction.APPROVED}
						{...props}
					/>
				</authz.Protected>
			)
		}
	}, [])

	return (
		<layouts.containers.flex flexDirection="column-reverse" flex="1" mb="25px">
			<layouts.loading.pending className="brandguard-working-area" loading={loading} flex="1" overflow="hidden">
				<layouts.containers.flex className="center-panel" flex="1" flexDirection="column">
					{documents.items.length === 0 && <bgscores.layouts.PreviewZero />}
					{selectedItem && (
						<layouts.containers.flex
							flex="1"
							flexDirection="row"
							border={layouts.theme.borders.grey.dark50alpha}
							boxShadow={layouts.theme.boxshadow.black.wide}
						>
							<layouts.containers.flex
								className="left-document-side"
								flex="6"
								borderRight={layouts.theme.borders.grey.dark50alpha}
								alignItems="center"
								justifyContent="center"
								background={layouts.theme.backgrounds.bluealpha5}
								position="relative"
							>
								<layouts.overlays.Screen enabled>
									<layouts.loading.screen loading={pageImageLoading} flex="1" height="100%">
										<PDFContainer>
											{currentPage && pageImageURL ? (
												<img
													src={pageImageURL}
													alt={`Page ${currentPageIndex + 1}`}
													style={{
														maxWidth: "100%",
														maxHeight: "100%",
														objectFit: "contain",
														display: "block"
													}}
												/>
											) : (
												<layouts.containers.flex
													flexDirection="column"
													alignItems="center"
													justifyContent="center"
													p="20px"
												>
													<icons.brandguard.document width="80px" height="80px" />
													<layouts.Span fontSize="16px" fontWeight="600" mb="10px">
														Page {currentPageIndex + 1} of {selectedItem.pages?.length || 0}
													</layouts.Span>
													<layouts.Span fontSize="14px" color={layouts.theme.colors.grey.dark50}>
														Document: {selectedItem.media?.description || selectedItem.media?.id}
													</layouts.Span>
													{!currentPage && <layouts.Span fontSize="12px" mt="10px" color={layouts.theme.colors.grey.dark50}>Loading page...</layouts.Span>}
													{currentPage && !pageImageURL && !pageImageLoading && <layouts.Span fontSize="12px" mt="10px" color={layouts.theme.colors.grey.dark50}>Page image not available</layouts.Span>}
												</layouts.containers.flex>
											)}
										</PDFContainer>
									</layouts.loading.screen>
								</layouts.overlays.Screen>

								{/* Horizontal document navigation arrows */}
								{documents.items.length > 1 && (
									<>
										<layouts.containers.absolute left="-20px">
											<layouts.Flex
												background={`${layouts.theme.colors.white}BF`}
												boxShadow="0px 1px 2px 0px #00000040"
												alignItems="center"
												justifyContent="center"
												borderRadius="50%"
												width="40px"
												height="40px"
												cursor="pointer"
												onClick={prevDocument}
											>
												<icons.arrows.ArrowLeft />
											</layouts.Flex>
										</layouts.containers.absolute>
										<layouts.containers.absolute right="-20px">
											<layouts.Flex
												background={`${layouts.theme.colors.white}BF`}
												boxShadow="0px 1px 2px 0px #00000040"
												alignItems="center"
												justifyContent="center"
												borderRadius="50%"
												width="40px"
												height="40px"
												cursor="pointer"
												onClick={nextDocument}
											>
												<icons.arrows.ArrowRight />
											</layouts.Flex>
										</layouts.containers.absolute>
									</>
								)}

								{/* Vertical page navigation arrows */}
								{selectedItem.pages && selectedItem.pages.length > 1 && (
									<>
										<layouts.containers.absolute top="20px">
											<layouts.Flex
												background={`${layouts.theme.colors.white}BF`}
												boxShadow="0px 1px 2px 0px #00000040"
												alignItems="center"
												justifyContent="center"
												borderRadius="50%"
												width="40px"
												height="40px"
												cursor="pointer"
												onClick={prevPage}
												opacity={currentPageIndex === 0 ? 0.3 : 1}
											>
												<icons.arrows.ArrowUp />
											</layouts.Flex>
										</layouts.containers.absolute>
										<layouts.containers.absolute bottom="20px">
											<layouts.Flex
												background={`${layouts.theme.colors.white}BF`}
												boxShadow="0px 1px 2px 0px #00000040"
												alignItems="center"
												justifyContent="center"
												borderRadius="50%"
												width="40px"
												height="40px"
												cursor="pointer"
												onClick={nextPage}
												opacity={currentPageIndex === (selectedItem.pages.length - 1) ? 0.3 : 1}
											>
												<icons.arrows.ArrowDown />
											</layouts.Flex>
										</layouts.containers.absolute>
									</>
								)}
							</layouts.containers.flex>

							<layouts.containers.flex
								flex="5"
								py="10px"
								pl="40px"
								pr="20px"
								className={classnames("right-score-side", bgscores.layouts.styledscores)}
								background={layouts.theme.colors.white}
							>
								<layouts.Flex flex="1" minHeight="0px" height="calc(100vh - 250px)">
									<layouts.Flex flex="1" flexDirection="column" overflowY="auto" pr="10px" className={styledscroll}>
										<bgscores.layouts.accordion.Container initOpen title="Asset Scores">
											<bgdocuments.Scores item={selectedItem} currentPage={currentPage} />
										</bgscores.layouts.accordion.Container>
							{selectedItem.event && (
								<FeedbackPanel eventType="document" eventIds={[selectedItem.event.id]} />
							)}
										<bgapprovals.status.default>
											<bgapprovals.status.Uploads
												item={selectedItem}
												previtemstate={undoItems.find((u) => u.media?.id === selectedItem.media?.id)}
												agree={agree}
												disagree={disagree}
												undo={undo}
												borderRadius="10px"
											/>
										</bgapprovals.status.default>
										<bgscores.layouts.accordion.Container title="More Assets">
											<layouts.containers.ResponsiveGrid gap={10} breakpointCols={3} py="10px" px="5px">
												{documents.items.map((item) => {
													return (
														<bgdocuments.Display
															cardClassName={classnames(bgscores.layouts.styledcardimage)}
															key={item.media?.id}
															item={item}
															active={selectedItem && item?.media?.id === selectedItem.media?.id}
															onCardClick={() => {
																setSelectedItem(item)
																setCurrentPageIndex(0)
															}}
															onChange={onDocumentUpdate}
															panel={
																<authz.Protected enabled={settings.confidence !== State.DISABLED}>
																	<layouts.containers.absolute>
																		<layouts.Flex
																			background={layouts.theme.colors.white}
																			borderRadius="5px"
																			padding="5px 10px"
																			fontSize="12px"
																			fontWeight="600"
																		>
																			Score: {item.event?.doc_brand_voice !== undefined && item.event.doc_brand_voice >= 0
																				? `${Math.round(item.event.doc_brand_voice * 100)}%`
																				: 'N/A'}
																		</layouts.Flex>
																	</layouts.containers.absolute>
																</authz.Protected>
															}
															find={api.documents.uploads.find}
														/>
													)
												})}
											</layouts.containers.ResponsiveGrid>
										</bgscores.layouts.accordion.Container>
										{versionHistory.length > 1 && (
											<bgapprovals.VersionHistory versions={versionHistory} current={selectedItem.media?.id || ""} onVersionClick={(v) => setSelectedItem(v as api.DocumentSearchResponseItem)} />
										)}
										<layouts.containers.flex className="actions"></layouts.containers.flex>
										<authz.Protected enabled={permission.brandguard_upload}>
											<bgdocuments.MultiActions
												uploadItems={[selectedItem]}
												deleteAction={permission.brandguard_upload ? api.documents.uploads.patch : undefined}
												onDelete={(results) => {
													setSelectedItem(undefined)
													setDocuments((prevState) => ({
														...prevState,
														items: prevState.items.filter(
															(i) => !results.map((el) => el.media!.id).includes(i.media!.id),
														),
													}))
												}}
											>
												<layouts.containers.flex
													className="revise-action"
													title="Revise"
													background={isRevising ? layouts.theme.colors.grey.dark50 + "40" : "transparent"}
													onClick={() => {
														setIsRevising(true)
														setUploadFocused(true)
													}}
												>
													<icons.UnstyledFileUpload width="18" height="18" />
												</layouts.containers.flex>
											</bgdocuments.MultiActions>
										</authz.Protected>
									</layouts.Flex>
								</layouts.Flex>
							</layouts.containers.flex>
						</layouts.containers.flex>
					)}
				</layouts.containers.flex>
			</layouts.loading.pending>
			<layouts.containers.flex flexDirection="column" mb="5px" zIndex="0" position="relative">
				<layouts.buttons.outline
					width="150px"
					height="40px"
					borderRadius="4px"
					onClick={() => setUploadFocused(!uploadFocused)}
					mb="5px"
				>
					<icons.Plus height="20px" width="20px" stroke={layouts.theme.colors.blue.blue} />
					<layouts.Span px="10px">Upload Assets</layouts.Span>
				</layouts.buttons.outline>
				<layouts.containers.flex display={uploadFocused ? "flex" : "none"} position="absolute" top="100%">
					<UploadSimple
						className={classnames(nav.layouts.slideDownAnimation)}
						isProcessing={(d: api.DocumentSearchResponseItem) => predictions.processing(d.event?.approved)}
						onChange={(doc) => {
							setDocuments((prevState) => ({ ...prevState, items: [...[doc], ...prevState.items] }))
							setSelectedItem(doc)
							setIsRevising(false)
						}}
						resume={() => {
							const searchReq = {
								offset: 0n,
								limit: 20n,
								brand_id: brand.id,
								queues: [uuid.NIL, brand.id],
								approved: [api.Prediction.NONE],
							} as api.DocumentSearchRequest
							return api.documents.uploads
								.search(searchReq.brand_id, searchReq, bearertoken)
								.then((docs) => {
									return docs.items.map(
										(d) =>
											({
												id: uuid.v4(),
												display: d.media?.description,
												background: layouts.theme.colors.blue.blue,
												data: d,
											}) as inputs.uploads.api.uploaded<api.DocumentSearchResponseItem>,
									)
								})
						}}
						refresh={(data) => {
							return api.documents.uploads
								.find(data.media?.id!, brand.id, bearertoken)
								.then((resp) => {
									return resp.event?.approved === api.Prediction.ERROR
										? CancellablePromise.reject(resp)
										: CancellablePromise.resolve(resp)
								})
						}}
						upload={(stats, content) =>
							isRevising ? api.documents.uploads.revise(content, brand.id, selectedItem!.media!.id, authzc.bearer(authzaccount)) : api.documents.uploads.create(content, brand.id, authzc.bearer(authzaccount))
						}
						DropWell={MemoUpload}
						onClose={() => { setUploadFocused(false); setIsRevising(false) }}
					/>
				</layouts.containers.flex>
			</layouts.containers.flex>
		</layouts.containers.flex>
	)
}
